import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useContext } from "react";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@iconify/react";
import { CartContext } from "../context/cart";
import { cn } from "@/lib/utils";

export function CartProduct({ product }) {
  const { setCart } = useContext(CartContext);
  const [mods, setMods] = useState(product.custom_mods || []);
  const [showDetails, setShowDetails] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceValue, setPriceValue] = useState(String(product.custom_price_manual ?? product.price ?? product.custom_price));

  const manualBase = product.custom_price_manual;

  useEffect(() => {
    setPriceValue(String(manualBase ?? product.price ?? product.custom_price));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualBase, product.price]);

  useEffect(() => {
    setCart(prev => prev.map(p => {
      if (p.custom_id === product.custom_id) {
        const newCustomId = mods.length > 0 ? product.id + '-' + mods.map(m => m.id).join('-') : product.id;
        const base = typeof p.custom_price_manual === 'number' ? p.custom_price_manual : (p.price ?? p.custom_price);
        return {
          ...p,
          custom_price: base + mods.reduce((total, mod) => total + mod.price, 0),
          custom_mods: mods,
          custom_id: newCustomId,
        };
      }
      return p;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mods]);

  const incrementQuantity = (e) => {
    e.stopPropagation();
    setCart(prev => prev.map(item => {
      if (item.custom_id === product.custom_id) {
        return { ...item, quantity: item.quantity + 1 };
      }
      return item;
    }));
  };

  const decrementQuantity = (e) => {
    e.stopPropagation();
    setCart(prev => prev
      .map(item => {
        if (item.custom_id === product.custom_id) {
          return { ...item, quantity: item.quantity - 1 };
        }
        return item;
      })
      .filter(item => item.quantity > 0));
  };

  const deleteProduct = (e) => {
    e.stopPropagation();
    setShowDetails(false);
    setCart(prev => prev.filter(item => item.custom_id !== product.custom_id));
  };

  const commitPrice = () => {
    const parsed = parseFloat(priceValue);
    if (!isNaN(parsed) && parsed >= 0) {
      const modsTotal = (product.custom_mods || []).reduce((total, mod) => total + mod.price, 0);
      setCart(prev => prev.map(item => {
        if (item.custom_id === product.custom_id) {
          return { ...item, custom_price_manual: parsed, custom_price: parsed + modsTotal };
        }
        return item;
      }));
    } else {
      setPriceValue(String(manualBase ?? product.price ?? product.custom_price));
    }
    setEditingPrice(false);
  };

  const hasMods = product.mods && product.mods.some(col => col.mod_collection?.mods?.length > 0);
  const currentPrice = product.custom_price;

  return (
    <div className="w-full bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden select-none" onClick={() => setShowDetails(!showDetails)}>
      {/* Main row - always visible */}
      <div className="flex items-center gap-2 p-2">
        {/* Product image */}
        <div className="shrink-0">
          {product?.image ? (
            <img
              src={product.image}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-11 w-11 rounded-lg object-cover"
            />
          ) : (
            <div className="flex items-center justify-center w-11 h-11 bg-neutral-100 text-neutral-400 rounded-lg">
              <Icon icon="mdi:food" className="text-xl" />
            </div>
          )}
        </div>

        {/* Name + unit price + mods */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{product.name}</div>
          <div className="flex items-center gap-1 flex-wrap mt-0.5">
            <span className="text-xs text-neutral-400">{currentPrice.toFixed(2)}€/ud</span>
            {mods.map((mod, i) => (
              <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">
                {mod.defaultEnabled ? 'Sin ' : '+'}{mod.name}
              </Badge>
            ))}
          </div>
        </div>

        {/* Line total */}
        <div className="shrink-0 text-right w-20">
          <div className="font-bold text-base leading-tight">{(currentPrice * product.quantity).toFixed(2)}€</div>
        </div>

        {/* Quantity stepper */}
        <div className="flex items-center shrink-0 rounded-lg overflow-hidden border border-neutral-200" onClick={e => e.stopPropagation()}>
          <button
            onClick={decrementQuantity}
            className="w-9 h-10 bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 flex items-center justify-center transition-colors"
          >
            <Icon icon="mdi:minus" className="text-lg" />
          </button>
          <div className="w-9 h-10 bg-white border-x border-neutral-200 flex items-center justify-center text-base font-bold">
            {product.quantity}
          </div>
          <button
            onClick={incrementQuantity}
            className="w-9 h-10 bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 flex items-center justify-center transition-colors"
          >
            <Icon icon="mdi:plus" className="text-lg" />
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {showDetails && (
        <div className="px-3 pb-3 space-y-2 animate-fade animate-once [animation-duration:150ms]" onClick={e => e.stopPropagation()}>
          <div className="h-px bg-neutral-100" />

          {/* Price editor */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-neutral-500 shrink-0">Precio</span>
            {editingPrice ? (
              <div className="flex-1 flex items-center gap-1">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  autoFocus
                  value={priceValue}
                  onChange={e => setPriceValue(e.target.value)}
                  onBlur={commitPrice}
                  onKeyDown={e => e.key === 'Enter' && commitPrice()}
                  className="flex-1 h-10 text-base focus-visible:ring-blue-500"
                />
                <span className="text-sm text-neutral-400">€</span>
              </div>
            ) : (
              <button
                onClick={() => setEditingPrice(true)}
                className="flex-1 h-10 rounded-lg bg-neutral-50 border border-neutral-200 px-4 text-left text-base active:bg-neutral-100 transition-colors"
              >
                {(manualBase ?? product.price ?? product.custom_price).toFixed(2)}€
              </button>
            )}
          </div>

          {/* Mods */}
          {hasMods && product.mods.map((col, i) => {
            const collection = col.mod_collection;
            if (!collection?.mods?.length) return null;

            return (
              <div key={i}>
                <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-1.5">{collection.name}</div>
                <div className="space-y-1.5">
                  {collection.mods.map((item, j) => {
                    const isDefault = Boolean(item.defaultEnabled);
                    const isOn = mods.some(p => p.id == item.id);
                    const isActive = isOn ? !isDefault : isDefault;

                    return (
                      <button
                        key={j}
                        type="button"
                        onClick={() => {
                          const next = !isActive;
                          if (next === isDefault) {
                            setMods(prev => prev.filter(m => m.id != item.id));
                          } else {
                            setMods(prev => [...prev, item]);
                          }
                        }}
                        className={cn(
                          "w-full h-10 rounded-lg border-2 px-4 flex items-center justify-between gap-2 transition-all active:scale-[0.98]",
                          isActive
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-neutral-200 text-neutral-800 hover:bg-neutral-50"
                        )}
                      >
                        <span className="flex items-center gap-2 text-sm font-medium">
                          <Icon
                            icon={isActive ? "mdi:check-circle" : "mdi:checkbox-blank-circle-outline"}
                            className={cn("text-xl", isActive ? "text-white" : "text-neutral-300")}
                          />
                          {item.name}
                        </span>
                        {item.price ? (
                          <span className={cn("text-sm font-semibold", isActive ? "text-blue-100" : "text-neutral-500")}>
                            {item.price.toFixed(2)}€
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Delete */}
          <Button variant="destructive" className="w-full h-10 text-sm" onClick={deleteProduct}>
            <Icon icon="mdi:trash-can" className="mr-2 text-lg" />
            Eliminar
          </Button>
        </div>
      )}
    </div>
  );
}